/**
 * /ndt-inspection/reports — the inspection report register
 * (GET /ndt-inspection/reports, fieldiq-api#1152).
 *
 * Full width; a row opens the report on its own page. The filters (status
 * chip, kind, job, search) live in the URL so the back arrow returns to the
 * list as it was left (pages/downhole/recordPage.ts).
 *
 * CHIP COUNTS are a tally of the UNFILTERED list — honest because the
 * endpoint is unpaginated (LIMIT 500) and no endpoint returns per-status
 * counts. Weld and exam counts per row are the API's stats.
 *
 * New reports are made from a JOB (the Reports section on the job's page),
 * because a report is always one job's — so the header action leads there.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, FilePen, FileText, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useNdtJobs, useNdtReports } from '../../hooks/useNdtInspection';
import { queryLoadFailure } from '../../lib/load-failure';
import { NDT_REPORT_STATUS_SKIN, ndtReportStatusSkin } from '../../lib/ndt-reports';
import { NDT_REPORT_STATUSES, type NdtReport } from '../../types/ndt-inspection';
import { useOpenRecord } from '../downhole/recordPage';
import { NDT_INPUT, NdtChip, NdtChipRow, NdtEmpty, NdtPanel, NdtRegister } from './NdtBits';
import { NdtReportCard, ndtReportColumns } from './NdtReportBits';

const STATUS_CHIPS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  ...NDT_REPORT_STATUSES.map((s) => ({ key: s, label: NDT_REPORT_STATUS_SKIN[s].label })),
];

export default function ReportsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const kind = params.get('kind') ?? '';
  const jobId = params.get('job') ?? '';
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const listQuery = useNdtReports({ status: status || undefined, kind: kind || undefined, jobId: jobId || undefined, q: q || undefined });
  const allQuery = useNdtReports();
  const jobs = useNdtJobs();
  const reports = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (r: NdtReport) => boolean) => (all ? all.filter(pick).length : undefined);
  const filtered = Boolean(status || kind || jobId || q);

  return (
    <div data-testid="ndt-reports-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="Inspection Reports"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button type="button" onClick={() => navigate('/ndt-inspection/jobs')} className="inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10">
            Jobs →
          </button>
        }
        stats={[
          { icon: <FileText className="size-4" aria-hidden="true" />, label: 'Reports', sub: 'On record', tone: 'neutral', loading: allQuery.isLoading, value: displayValue(countOf(() => true)) },
          { icon: <FilePen className="size-4" aria-hidden="true" />, label: 'Drafts', sub: 'Not issued', tone: 'accent', loading: allQuery.isLoading, value: displayValue(countOf((r) => r.status === 'draft')) },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Issued', sub: 'Numbered', tone: 'neutral', loading: allQuery.isLoading, value: displayValue(countOf((r) => r.status === 'issued')) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">A report is made from its job. Issuing numbers it and freezes what it says.</p>
          <NdtChipRow label="Filter by status">
            {STATUS_CHIPS.map((c) => (
              <NdtChip
                key={c.key || 'all'}
                label={c.label}
                active={status === c.key}
                count={all ? countOf((r) => !c.key || r.status === c.key) : undefined}
                onClick={() => setParam('status', c.key)}
              />
            ))}
          </NdtChipRow>
          <div className="grid gap-3 md:grid-cols-[1fr_200px_240px]">
            <label className="relative block">
              <span className="sr-only">Search report, job, customer or title</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search report, job, customer or title"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select aria-label="Kind" className={NDT_INPUT} value={kind} onChange={(e) => setParam('kind', e.target.value)}>
              <option value="">All kinds</option>
              <option value="final">Final</option>
              <option value="interim">Interim</option>
              <option value="daily">Daily</option>
            </select>
            <select aria-label="Job" className={NDT_INPUT} value={jobId} onChange={(e) => setParam('job', e.target.value)}>
              <option value="">All jobs</option>
              {(jobs.data?.data ?? []).map((j) => (
                <option key={j.id} value={j.id}>
                  {j.jobNumber}
                  {j.customerName ? ` · ${j.customerName}` : ''}
                </option>
              ))}
            </select>
          </div>

          <NdtPanel
            eyebrow="Report register"
            title="Reports"
            testId="ndt-report-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && reports.length > 0
                ? filtered && all
                  ? `Showing ${reports.length} of ${all.length} reports`
                  : `${reports.length} report${reports.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <NdtRegister
              query={listQuery}
              rows={reports}
              columns={ndtReportColumns({ showJob: true })}
              rail={(r) => ndtReportStatusSkin(r.status).rail}
              onOpen={(r) => openRecord(`/ndt-inspection/reports/${r.id}`)}
              subject="inspection reports"
              testId="ndt-report"
              minWidth="min-w-[960px]"
              empty={
                filtered ? (
                  <NdtEmpty>No report matches these filters.</NdtEmpty>
                ) : (
                  <NdtEmpty>No inspection reports are on record. A final report is drafted when a job is completed.</NdtEmpty>
                )
              }
              card={(r) => <NdtReportCard r={r} showJob />}
            />
          </NdtPanel>
        </div>
      </section>
    </div>
  );
}
